import { repositoryFactory, DataSourceStrategy, CacheStrategy } from '@rammetec/data-access';
import { rtLogService } from '@rammetec/core';
import { MODULE_GROUP } from '../types/moduleGroup.js';
import { ORGANIZATION_MODULE } from '../types/module.js';
import { assertNoGroupCycle } from '../utils/moduleResolution.js';
import { isProvisionalTargetId, planModuleGroupCopy, } from '../utils/moduleGroupCopy.js';
import { normalizeTags } from '../utils/moduleTags.js';
class ModuleGroupService {
    async getRepository(orgId) {
        return repositoryFactory.getRepository(`org/${orgId}/module-groups`, MODULE_GROUP, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
    }
    async getOrgModulesRepository(orgId) {
        return repositoryFactory.getRepository(`org/${orgId}/modules`, ORGANIZATION_MODULE, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
    }
    async getGroups(orgId) {
        try {
            const repository = await this.getRepository(orgId);
            return await repository.getAll();
        }
        catch (error) {
            rtLogService.error('Error fetching module groups', { orgId, error });
            throw error;
        }
    }
    async createGroup(orgId, data) {
        try {
            const repository = await this.getRepository(orgId);
            const existing = await repository.getAll();
            const provisionalId = data.id ?? '__new__';
            assertNoGroupCycle(existing, provisionalId, data.parentGroupId);
            const payload = {
                ...(data.id ? { id: data.id } : {}),
                name: data.name,
                ...(data.icon != null ? { icon: data.icon } : {}),
                ...(data.description != null ? { description: data.description } : {}),
                ...(data.order != null ? { order: data.order } : {}),
                ...(data.parentGroupId != null ? { parentGroupId: data.parentGroupId } : {}),
                ...(data.autoTags != null ? { autoTags: normalizeTags(data.autoTags) } : {}),
            };
            const created = await repository.create(payload);
            rtLogService.info('Module group created', { orgId, groupId: created.id, name: data.name });
            return created;
        }
        catch (error) {
            rtLogService.error('Error creating module group', { orgId, error });
            throw error;
        }
    }
    async updateGroup(orgId, groupId, delta) {
        try {
            const repository = await this.getRepository(orgId);
            if ('parentGroupId' in delta) {
                const existing = await repository.getAll();
                assertNoGroupCycle(existing, groupId, delta.parentGroupId);
            }
            const withTags = delta.autoTags != null
                ? { ...delta, autoTags: normalizeTags(delta.autoTags) }
                : { ...delta };
            await repository.update(groupId, withTags);
            rtLogService.info('Module group updated', { orgId, groupId });
        }
        catch (error) {
            rtLogService.error('Error updating module group', { orgId, groupId, error });
            throw error;
        }
    }
    /**
     * Deletes a group only when it has no child groups and no OrganizationModules.
     * Caller must reassign children first.
     */
    async deleteGroup(orgId, groupId) {
        try {
            const [groupsRepo, orgModulesRepo] = await Promise.all([
                this.getRepository(orgId),
                this.getOrgModulesRepository(orgId),
            ]);
            const [groups, orgModules] = await Promise.all([
                groupsRepo.getAll(),
                orgModulesRepo.getAll(),
            ]);
            const childGroups = groups.filter((g) => g.parentGroupId === groupId);
            const childModules = orgModules.filter((om) => om.groupId === groupId);
            if (childGroups.length > 0 || childModules.length > 0) {
                throw new Error(`Cannot delete module group ${groupId}: reassign ${childGroups.length} child group(s) and ${childModules.length} module(s) first`);
            }
            await groupsRepo.delete(groupId);
            rtLogService.info('Module group deleted', { orgId, groupId });
        }
        catch (error) {
            rtLogService.error('Error deleting module group', { orgId, groupId, error });
            throw error;
        }
    }
    /**
     * Copy menu groups from a model org into `targetOrgId` (one-shot, no live link).
     * Reuses same-name groups at the mapped parent (union autoTags only); places
     * target root OrganizationModules that match a grouped source moduleId.
     * Does not create OrganizationModules — that remains `syncPlans`. Request 0345.
     */
    async copyFromOrg(sourceOrgId, targetOrgId) {
        try {
            if (sourceOrgId === targetOrgId) {
                throw new Error('copyFromOrg: source and target organization must differ');
            }
            const [sourceGroups, targetGroups, sourceModules, targetModules, orgModulesRepo] = await Promise.all([
                this.getGroups(sourceOrgId),
                this.getGroups(targetOrgId),
                this.getOrgModulesRepository(sourceOrgId).then((r) => r.getAll()),
                this.getOrgModulesRepository(targetOrgId).then((r) => r.getAll()),
                this.getOrgModulesRepository(targetOrgId),
            ]);
            const plan = planModuleGroupCopy(sourceGroups, targetGroups, sourceModules, targetModules);
            /** Provisional `__new__:<sourceId>` → real target id after create. */
            const resolved = new Map();
            const resolveId = (id) => {
                if (!isProvisionalTargetId(id))
                    return id;
                const real = resolved.get(id);
                if (!real) {
                    throw new Error(`copyFromOrg: unresolved provisional group id ${id}`);
                }
                return real;
            };
            let groupsCreated = 0;
            let groupsReused = 0;
            for (const action of plan.actions) {
                if (action.kind === 'reuse') {
                    groupsReused += 1;
                    // May resolve a provisional id when two source groups share a name
                    // under the same parent and the first was planned as create.
                    const targetId = resolveId(action.targetId);
                    const existing = targetGroups.find((g) => g.id === targetId);
                    const current = normalizeTags(existing?.autoTags ?? []);
                    const same = current.length === action.autoTagsUnion.length &&
                        current.every((t, i) => t === action.autoTagsUnion[i]);
                    if (!same) {
                        await this.updateGroup(targetOrgId, targetId, {
                            autoTags: action.autoTagsUnion,
                        });
                    }
                    continue;
                }
                const parentGroupId = action.parentTargetId != null ? resolveId(action.parentTargetId) : undefined;
                const created = await this.createGroup(targetOrgId, {
                    name: action.name,
                    ...(action.icon != null ? { icon: action.icon } : {}),
                    ...(action.description != null ? { description: action.description } : {}),
                    ...(action.order != null ? { order: action.order } : {}),
                    ...(parentGroupId != null ? { parentGroupId } : {}),
                    ...(action.autoTags != null ? { autoTags: action.autoTags } : {}),
                });
                resolved.set(`__new__:${action.sourceId}`, created.id);
                groupsCreated += 1;
            }
            let modulesPlaced = 0;
            for (const placement of plan.placements) {
                const groupId = resolveId(placement.groupId);
                await orgModulesRepo.update(placement.orgModuleId, {
                    groupId,
                    ...(placement.order != null ? { order: placement.order } : {}),
                });
                modulesPlaced += 1;
            }
            const result = { groupsCreated, groupsReused, modulesPlaced };
            rtLogService.info('Module groups copied from org', {
                sourceOrgId,
                targetOrgId,
                ...result,
            });
            return result;
        }
        catch (error) {
            rtLogService.error('Error copying module groups from org', {
                sourceOrgId,
                targetOrgId,
                error,
            });
            throw error;
        }
    }
}
export const moduleGroupService = new ModuleGroupService();
/** Re-export for dialogs that validate before save (0149). */
export { assertNoGroupCycle } from '../utils/moduleResolution.js';
//# sourceMappingURL=moduleGroupService.js.map